import { Helmet, HelmetData } from "react-helmet-async";
import { useRef } from "react";
import {
  AI_BG_NEW,
  Assessment_icon,
  Automated_Quality_Icon,
  Build_And_Deploy_Icon,
  Data_Pipeline_Architecture_Icon,
  Data_Pipeline_Hero_Img,
  Data_Versioning,
  Flexible_Architecture_Img,
  Infrastucture_Icon,
  Managed_Operations_Icon,
  Pipeline_Assessment_Icon,
  Proactive_Monitoring,
  Production_Ready_Img,
  Services_Bg,
  Testing_Icon,
  Use_Case_Bg,
} from "../../images";
import BookConsultation from "../book-a-consultation";
import { NewFooter } from "../newFooter";
import useIntersectionObserver from "../../utils/useIntersectionObserver";

export default function DataPipelineAndEvaluation() {
  const targetRef = useRef(null);

  useIntersectionObserver({ targetRef });

  const helmetData = new HelmetData({});

  const features = [
    {
      icon: Data_Pipeline_Architecture_Icon,
      title: "Data Pipeline Architecture",
      description:
        "Custom-designed pipelines that automate data collection, processing, and delivery. We build robust systems that handle everything.",
    },
    {
      icon: Automated_Quality_Icon,
      title: "Automated Quality Checks",
      description:
        "Built-in validation at every pipeline stage. We catch data issues early, ensuring only clean, high-quality data reaches your models.",
    },
    {
      icon: Data_Versioning,
      title: "Data Versioning",
      description:
        "Complete tracking of data transformations. You'll always know exactly what data trained which model.",
    },
  ];

  const process = [
    {
      icon: Assessment_icon,
      title: "Assessment & Planning",
      description:
        "We analyze your workflows, identify bottlenecks, and design pipeline architecture that addresses your specific challenges.",
    },
    {
      icon: Infrastucture_Icon,
      title: "Infrastructure Setup",
      description:
        "We build and deploy your pipeline infrastructure with security, scalability, and maintainability as core principles.",
    },
    {
      icon: Testing_Icon,
      title: "Testing & Validation",
      description:
        "Rigorous testing ensures pipelines handle edge cases and maintain data quality under various conditions",
    },
  ];

  const services = [
    {
      id: 1,
      title: "Production-Ready Reliability",
      description:
        "Pipelines built for real-world demands—handling failures gracefully, scaling automatically, and maintaining data.",
      image: Production_Ready_Img,
    },
    {
      id: 2,
      title: "Flexible Architecture",
      description:
        "We adapt our solutions to work with cloud platforms, on-premise systems, or hybrid environments.",
      image: Flexible_Architecture_Img,
    },
    {
      id: 3,
      title: "Proactive Monitoring",
      description:
        "Systems that alert you to issues before they become problems, with clear diagnostics and remediation paths.",
      image: Proactive_Monitoring,
    },
  ];

  const why = [
    {
      icon: Pipeline_Assessment_Icon,
      title: "Pipeline Assessment",
      text: "Comprehensive audit of your current data operations with actionable recommendations.",
    },
    {
      icon: Build_And_Deploy_Icon,
      title: "Build & Deploy",
      text: "Full pipeline development from design through deployment, customized for your infrastructure.",
    },
    {
      icon: Managed_Operations_Icon,
      title: "Managed Operations",
      text: "We run your data pipelines end-to-end, so you can focus on AI development.",
    },
  ];
  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>Data Pipelines &amp; Continuous AI Evaluation Africa | Reliable AI Infrastructure | Analytics Intelligence Africa</title>

        <link
          rel="canonical"
          href="https://africa.analyticsintelligence.com/data-pipeline-and-evaluation"
        />

        <meta
          name="description"
          content="Analytics Intelligence Africa designs and manages robust data pipelines and continuous evaluation frameworks that keep AI systems accurate, reliable, and scalable — from training through deployment and ongoing improvement across African enterprises."
        />

        <meta
          name="keywords"
          content="Analytics Intelligence Africa, data pipelines Africa, AI data infrastructure Africa, continuous AI evaluation Africa, MLOps Africa, machine learning pipelines Africa, data operations AI Africa, AI reliability Africa, AI monitoring Africa, evaluation frameworks Africa, AI deployment Africa, data versioning Africa"
        />

        <meta property="og:title" content="Data Pipelines & Continuous AI Evaluation | Analytics Intelligence Africa" />
        <meta property="og:description" content="End-to-end data pipelines and continuous evaluation systems that ensure consistent, high-performing AI models at scale — Analytics Intelligence Africa." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://africa.analyticsintelligence.com/data-pipeline-and-evaluation" />
        <meta property="og:image" content="https://africa.analyticsintelligence.com/favicon.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Data Pipelines & Continuous AI Evaluation | Analytics Intelligence Africa" />
        <meta name="twitter:description" content="End-to-end data pipelines and continuous evaluation systems that ensure consistent, high-performing AI models at scale — Analytics Intelligence Africa." />
        <meta name="twitter:image" content="https://africa.analyticsintelligence.com/favicon.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Data Pipelines & Continuous AI Evaluation",
            "url": "https://africa.analyticsintelligence.com/data-pipeline-and-evaluation",
            "description": "Designing and managing robust data pipelines and continuous evaluation frameworks that keep AI systems accurate, reliable, and scalable for African enterprises.",
            "provider": {
              "@type": "Organization",
              "name": "Analytics Intelligence Africa",
              "url": "https://africa.analyticsintelligence.com"
            },
            "areaServed": ["Nigeria", "Ghana", "Kenya", "South Africa", "Rwanda", "Africa"]
          }
        `}</script>
      </Helmet>

      <main>
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundSize: "cover",
          }}
          className="min-h-[100vh] flex items-center bg-left-bottom md:bg-center"
          ref={targetRef}
        >
          <div className="container mx-auto w-full h-full flex flex-col md:flex-row justify-between items-center gap-16 md:gap-20 lg:gap-28 xl:gap-36  pt-[126px] pb-11 md:py-[96px] py-[96px]">
            <div className="flex basis-2/4 flex-col flex-wrap gap-[16px] items-center md:items-start">
              <div className="space-y-8 md:space-y-11 flex flex-col items-center md:items-start">
                <span className="text-sm md:text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                  Data Pipelines
                </span>
                <div className="space-y-4">
                  <h2 className="text-4xl md:text-[40px] xl:text-[48px] text-center md:text-left font-extrabold leading-[1.3] md:leading-[54px] text-white">
                    Robust Data Pipelines & <br className="hidden xl:block" />{" "}
                    Continuous Evaluation
                  </h2>
                  <p className="text-sm md:text-lg text-center md:text-left font-medium text-[#E3E3E3] leading-5">
                    Building reliable data infrastructure and evaluation
                    frameworks that ensure your AI systems deliver consistent,
                    accurate results at scale
                  </p>
                </div>
              </div>
            </div>
            <div className="basis-2/4 flex justify-end">
              <img
                className=" w-full max-w-[680px]"
                src={Data_Pipeline_Hero_Img}
                alt="illustration"
              />
            </div>
          </div>
        </section>

        <section className="relative w-full bg-white py-[80px] md:py-[100px] overflow-hidden">
          <div className="mx-auto container">
            <div className="h-[300px] w-[300px] xl:h-[450px] xl:w-[450px] bg-[#7AA8FF] blur-[180px] absolute top-0 -right-[70%] md:-right-[45%] lg:-right-[15%]"></div>
            <div className="mb-10 md:mb-20 text-center">
              <h1 className="mb-6 text-2xl font-bold tracking-tight md:text-[36px]">
                Powering AI with Reliable Data Operations
              </h1>
              <p className="mx-auto max-w-5xl font-medium text-sm sm:text-base md:text-lg xl:text-xl text-gray-700">
                Great AI is built on solid data foundations. We design and
                manage end-to-end data pipelines and evaluation systems that
                keep your AI models performing at their best, from initial
                training through continuous improvement.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-y-12 md:gap-x-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="relative flex flex-col items-center text-center bg-[#EDF9FF29] border border-[#EDF9FF] px-6 py-[32px]"
                >
                  {/* Icon */}
                  <img
                    src={feature.icon}
                    alt="icon"
                    className="absolute -top-5"
                  />

                  {/* Title */}
                  <h3 className="text-lg font-bold mb-2">{feature.title}</h3>

                  {/* Description */}
                  <p className="text-base">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          className="relative py-20 bg-left-bottom md:bg-center bg-cover md:bg-cover"
          style={{
            backgroundImage: `url("${Services_Bg}")`,
          }}
        >
          <div className="container mx-auto">
            {/* Header */}
            <div className="mb-8 text-center">
              <h1 className="mb-2 text-2xl md:text-3xl font-bold tracking-tight xl:text-[36px] text-[#FFF]">
                What Sets Us Apart
              </h1>
            </div>

            {/* Service Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-y-6 md:gap-8">
              {services.map((service) => (
                <div
                  key={service.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 col-span-1 md:col-span-6 lg:col-span-4"
                >
                  {/* Image Container */}
                  <div className="relative h-[240px] md:h-72 flex items-center justify-center p-6">
                    <img
                      src={service.image || "/placeholder.svg"}
                      alt={service.title}
                      className="w-full h-full object-contain"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-lg md:text-2xl font-bold mb-2 md:mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-white py-[80px] md:py-[100px] relative overflow-hidden">
          <div className="mx-auto container">
            <div className="h-[300px] w-[300px] xl:h-[350px] xl:w-[350px] bg-[#7AA8FF] blur-[180px] absolute bottom-0 -right-[70%] md:-right-[45%] lg:-right-[15%]"></div>
            <div className="mb-10 md:mb-20 text-center">
              <h1 className="mb-6 text-2xl font-bold tracking-tight md:text-[36px]">
                Our Process
              </h1>
              <p className="mx-auto max-w-5xl font-medium text-sm sm:text-base md:text-lg xl:text-xl text-gray-700">
                Empowering Global AI with Africa’s Smartest Data Workforce.
              </p>
            </div>

            <div className="py-16 overflow-x-auto hide-scrollbar">
              <div className="grid grid-cols-3 gap-x-[140px] min-w-[1200px]">
                {process.map((feature, index) => (
                  <div
                    key={index}
                    className="relative flex flex-col items-center text-center bg-[#EDF9FF29] border border-[#EDF9FF] px-6 py-[32px] min-w-[320px]"
                  >
                    {/* Icon */}
                    <img
                      src={feature.icon}
                      alt="icon"
                      className="absolute -top-5"
                    />

                    <span className="text-[64px] text-[#0073FF] font-semibold absolute right-2 -top-12">
                      {index + 1}
                    </span>

                    {index === 0 && (
                      <svg
                        width="102"
                        height="29"
                        viewBox="0 0 102 29"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="absolute top-2 -right-[120px]"
                      >
                        <path
                          d="M1.99903 14.2875C16.9771 -0.935538 65.5992 -7.00031 99.4889 26.0245M99.4889 26.0245L79.7442 23.6474M99.4889 26.0245L97.5845 10.6468"
                          stroke="#0073FF"
                          strokeWidth="4"
                          strokeLinecap="round"
                        />
                      </svg>
                    )}
                    {index === 1 && (
                      <svg
                        width="103"
                        height="22"
                        viewBox="0 0 103 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="absolute bottom-2 -right-[120px]"
                      >
                        <path
                          d="M2 2.00012C15.0511 18.9044 62.5997 30.7374 100.194 2.00013M100.194 2.00013L80.3065 2.00013M100.194 2.00013L96.465 17.0399"
                          stroke="#0073FF"
                          strokeWidth="4"
                          strokeLinecap="round"
                        />
                      </svg>
                    )}

                    {/* Title */}
                    <h3 className="text-lg font-bold mb-2">{feature.title}</h3>

                    {/* Description */}
                    <p className="text-base">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section
          className="bg-[#EDF1FF] py-[48px] bg-left-bottom md:bg-center bg-cover md:bg-cover"
          style={{
            backgroundImage: `url("${Use_Case_Bg}")`,
          }}
        >
          <div className="container space-y-12">
            <div className="mb-10 text-center">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight xl:text-[36px] text-[#FFF]">
                Flexible Engagement Models
              </h1>
            </div>

            <div className="grid grid-cols-12 lg:flex gap-y-8 sm:gap-8 lg:justify-center">
              {why.map((item, idx) => (
                <div
                  key={idx}
                  className="col-span-12 sm:col-span-6 lg:max-w-[300px] bg-white flex flex-col items-center rounded-xl px-3 py-6 gap-4"
                >
                  <img
                    src={item.icon}
                    alt={`${item.title} illustration`}
                    className="w-16 h-16"
                  />
                  <div className="space-y-2 text-center">
                    <h5 className="text-lg md:text-xl font-bold text-[#020128]">
                      {item.title}
                    </h5>
                    <p className="text-sm md:text-base text-[#020128]">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <NewFooter>
          <BookConsultation
            title="Ready to Build Reliable AI Infrastructure?"
            text="Let's discuss how we can streamline your data operations and strengthen your evaluation frameworks."
            btnText="Contact Us"
            href="/contact"
          />
        </NewFooter>
      </main>
    </>
  );
}
